import React, { useContext, useState } from "react";
import { Button, CheckBox } from "@dealer-center/core-components";
import styled from "styled-components";
import LABELS from "../labels.json";
import {
  ALL_TABLE_ATTRIBUTES,
  TableAttributesContext
} from "../providers/TableAttributesProvider";
import { Locale } from "@dealer-center/ddc-core-utils";
import { capitalizeString } from "../utils/utils";

const StyledDiv = styled.div`
  padding: 0 16px;
`;

const ActionButtons = styled.div`
  padding: 24px 0px;
  display: flex;
  gap: 16px;
  justify-content: end;
`;

const ColumnsContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const Column = styled.div`
  flex: 1;
  list-style: none;
`;

const AttributeItem = styled.div`
  display: flex;
  gap: 12px;
  height: 36px;
`;
const FootnoteContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
`;
const FootnoteLabel = styled.label`
  font-size: 12px;
`;
const BottomContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

interface TableAttributesPopupProps {
  onCancel: () => void;
}

const splitIntoColumns = (items: string[], numColumns: number): string[][] => {
  const columns: string[][] = Array.from({ length: numColumns }, () => []);
  items.forEach((item, index) => {
    columns[Math.floor(index / Math.ceil(items.length / numColumns))].push(
      item
    );
  });
  return columns;
};

const TableAttributesPopup: React.FC<TableAttributesPopupProps> = ({
  onCancel
}) => {
  const { selectedAttributes, setSelectedAttributes } = useContext(
    TableAttributesContext
  );
  const [currentSelectedAttributes, setCurrentSelectedAttributes] = useState([
    ...selectedAttributes
  ]);
  const columns = splitIntoColumns(ALL_TABLE_ATTRIBUTES, 3);
  Locale.setLabelsOnce(LABELS);

  const onAttributeCheckChange = attribute => {
    if (currentSelectedAttributes.indexOf(attribute) > -1) {
      const newSelectedAttributes = currentSelectedAttributes.filter(
        attr => attr !== attribute
      );
      setCurrentSelectedAttributes(newSelectedAttributes);
    } else {
      const newSelectedAttributes = [...currentSelectedAttributes, attribute];
      setCurrentSelectedAttributes(newSelectedAttributes);
    }
  };

  return (
    <StyledDiv>
      <ColumnsContainer>
        {columns.map((column, colIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <Column key={colIndex}>
            {column.map((attribute, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <AttributeItem key={index}>
                <CheckBox
                  checked={currentSelectedAttributes.indexOf(attribute) > -1}
                  label={
                    <span>
                      {capitalizeString(Locale.getText(attribute) as string)}
                      &nbsp;
                      {attribute === "ACTION_NEEDED" && <sub>1</sub>}
                      {attribute === "SOURCE" && <sub>2</sub>}
                    </span>
                  }
                  value={attribute}
                  onChange={() => {
                    onAttributeCheckChange(attribute);
                  }}
                />
              </AttributeItem>
            ))}
          </Column>
        ))}
      </ColumnsContainer>
      <BottomContainer>
        <FootnoteContainer>
          <FootnoteLabel>
            (1) {Locale.getText("ACTION_NEEDED_FOOTNOTE")}
          </FootnoteLabel>
          <FootnoteLabel>(2) {Locale.getText("SOURCE_FOOTNOTE")}</FootnoteLabel>
        </FootnoteContainer>
        <ActionButtons>
          <Button
            analyticsConfiguration={{
              eventName: "",
              eventProperties: undefined
            }}
            buttonStyle="tertiary"
            onClick={onCancel}
          >
            {Locale.getText("CANCEL")}
          </Button>
          <Button
            analyticsConfiguration={{
              eventName: "",
              eventProperties: undefined
            }}
            onClick={() => {
              setSelectedAttributes(currentSelectedAttributes);
              onCancel();
            }}
          >
            {Locale.getText("SUBMIT")}
          </Button>
        </ActionButtons>
      </BottomContainer>
    </StyledDiv>
  );
};

export default TableAttributesPopup;
